import {AddToMergeQueueSection} from '../AddToMergeQueueSection'
import {DirectMergeActionsSection} from '../DirectMergeActionsSection'
import {DisableAutoMerge} from '../DisableAutoMerge'
import {MergeQueueInstructions} from './add-to-merge-queue/MergeQueueInstructions'
import {DirectMergeInstructions} from './direct-merge/DirectMergeInstructions'
import {DisableAutoMergeInstructions} from './disable-auto-merge/DisableAutoMergeInstructions'
import {useMergeControlInputs, useMergeControlState} from './merge-control-context'
import type {ButtonProps} from '@primer/react'

export type MergeControlControlProps = {
  buttonSize?: ButtonProps['size']
  directMergeConfirmation?: 'inline' | 'dialog'
  suppressInstructions?: boolean
  /**
   * Controls how the bypass toggle is rendered in relation to the MergeComponent
   * - 'none' (default) - callers will use `<MergeControl.BypassToggle />` externally
   * - 'menu-option' - bypass toggle will render in secondary button action lists within `MergeControl.Control`
   */
  bypassToggleRendering?: 'none' | 'menu-option'
}

function assertNeverControl(control: never): never {
  throw new Error(`Unexpected merge control: ${JSON.stringify(control)}`)
}

export function MergeComponent({
  buttonSize,
  directMergeConfirmation = 'inline',
  suppressInstructions = false,
  bypassToggleRendering = 'none',
}: MergeControlControlProps) {
  const {
    isAdminBypassToggleChecked,
    isBypassAllowed,
    isMergeable,
    isMergeQueueEnabled,
    mergeControlModel,
    showBypassToggle,
    currentStackEntryPositionNumber,
    stackSize,
  } = useMergeControlState()

  const {
    baseRefName,
    conflictsCondition,
    handleConfirmingMergeInfo,
    mergeCurrentlyBlockedForStacks,
    isConfirmingMergeInfo,
    mergeBoxRollupStatus,
    mergeRequirements: {
      commitMessageBody,
      commitMessageHeadline,
      defaultCommitAuthorEmail,
      possibleCommitAuthorEmails,
      state: mergeRequirementsState,
    },
    onStackMergePendingChange,
    setShouldFocusPrimaryMergeButton,
    shouldFocusPrimaryMergeButton,
    stackedEntriesToMergeCount,
    stackedEntries,
    entriesThatCanBeAutoMerged,
    status,
    isStacked,
    stackMetadata,
    pullRequest: {
      autoMergeRequest,
      isDraft,
      isInMergeQueue,
      mergeStateStatus,
      numberOfCommits,
      viewerMergeActions,
      viewerCanAddAndRemoveFromMergeQueue,
      viewerCanAddToMergeQueueSolo,
      viewerCanDisableAutoMerge,
    },
  } = useMergeControlInputs()

  const directMergeInstructions = suppressInstructions ? undefined : <DirectMergeInstructions />
  const autoMergeInstructions = suppressInstructions ? undefined : isMergeQueueEnabled ? (
    <MergeQueueInstructions />
  ) : (
    <DirectMergeInstructions />
  )
  const showBypassToggleOption = bypassToggleRendering === 'menu-option' && showBypassToggle

  switch (mergeControlModel.type) {
    case 'disable-auto-merge':
      return (
        <DisableAutoMerge
          autoMergeRangeEntries={isStacked ? stackedEntries?.filter(entry => entry.pull.isInAutoMergeRange) : undefined}
          baseRefName={baseRefName}
          buttonSize={buttonSize}
          confirmation={directMergeConfirmation}
          isMergeQueueEnabled={isMergeQueueEnabled}
          isStacked={isStacked}
          instructions={suppressInstructions ? undefined : <DisableAutoMergeInstructions />}
          viewerCanDisableAutoMerge={viewerCanDisableAutoMerge}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
        />
      )
    case 'inactive-direct':
      return (
        <DirectMergeActionsSection
          buttonSize={buttonSize}
          baseRefName={baseRefName}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          directMergeConfirmation={directMergeConfirmation}
          mergeable={false}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          instructions={directMergeInstructions}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={false}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          currentStackEntryPositionNumber={currentStackEntryPositionNumber}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
    case 'merge-queue-bypass':
      return (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          buttonSize={buttonSize}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          directMergeConfirmation={directMergeConfirmation}
          mergeable={isMergeable}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          instructions={directMergeInstructions}
          isAdminBypassToggleVisible={isBypassAllowed}
          isAdminBypassToggleChecked
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          showBypassToggleOption={showBypassToggleOption}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          currentStackEntryPositionNumber={currentStackEntryPositionNumber}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
    case 'merge-queue':
      return (
        <AddToMergeQueueSection
          autoMergeRequest={autoMergeRequest}
          baseRefName={baseRefName}
          buttonSize={buttonSize}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          instructions={suppressInstructions ? undefined : <MergeQueueInstructions />}
          isDraft={isDraft}
          isInMergeQueue={isInMergeQueue}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeRequirementsState={mergeRequirementsState}
          mergeStateStatus={mergeStateStatus}
          onStackMergePendingChange={onStackMergePendingChange}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          showBypassToggleOption={showBypassToggleOption}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          status={status}
          viewerCanAddAndRemoveFromMergeQueue={viewerCanAddAndRemoveFromMergeQueue}
          viewerCanAddToMergeQueueSolo={viewerCanAddToMergeQueueSolo}
          isStacked={isStacked}
          stackMetadata={stackMetadata}
          currentStackEntryPositionNumber={currentStackEntryPositionNumber}
          stackSize={stackSize}
        />
      )
    case 'auto-merge':
      return (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          buttonSize={buttonSize}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          directMergeConfirmation={directMergeConfirmation}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          mergeable={isMergeable}
          instructions={autoMergeInstructions}
          // A stack targeting a merge queue uses the queue's merge method for auto-merge, so there is no method to choose.
          hideMergeMethodOptions={isMergeQueueEnabled && isStacked}
          isMergeQueueEnabled={isMergeQueueEnabled}
          isAdminBypassToggleChecked={false}
          isAdminBypassToggleVisible={isBypassAllowed}
          // A stack can only merge once every pull request below this one is ready, so keep offering
          // auto-merge while any of them are still blocked, even if this pull request is mergeable.
          isAutoMergeAllowed={!isMergeable || Boolean(mergeCurrentlyBlockedForStacks)}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          showBypassToggleOption={showBypassToggleOption}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          entriesThatCanBeAutoMerged={entriesThatCanBeAutoMerged}
          stackMetadata={stackMetadata}
          currentStackEntryPositionNumber={currentStackEntryPositionNumber}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
    case 'direct':
      return (
        <DirectMergeActionsSection
          baseRefName={baseRefName}
          buttonSize={buttonSize}
          commitMessageBody={commitMessageBody}
          commitMessageHeadline={commitMessageHeadline}
          conflictsCondition={conflictsCondition}
          defaultCommitAuthorEmail={defaultCommitAuthorEmail}
          directMergeConfirmation={directMergeConfirmation}
          handleConfirmingMergeInfo={handleConfirmingMergeInfo}
          instructions={directMergeInstructions}
          isAdminBypassToggleChecked={isAdminBypassToggleChecked}
          isAdminBypassToggleVisible={isBypassAllowed}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          mergeable={isMergeable}
          mergeCurrentlyBlockedForStacks={mergeCurrentlyBlockedForStacks}
          isStacked={isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          numberOfCommits={numberOfCommits}
          onStackMergePendingChange={onStackMergePendingChange}
          possibleCommitAuthorEmails={possibleCommitAuthorEmails}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          showBypassToggleOption={showBypassToggleOption}
          stackedEntriesToMergeCount={stackedEntriesToMergeCount}
          stackMetadata={stackMetadata}
          currentStackEntryPositionNumber={currentStackEntryPositionNumber}
          stackSize={stackSize}
          status={status}
          viewerMergeActions={viewerMergeActions}
        />
      )
    default:
      return assertNeverControl(mergeControlModel.type satisfies never)
  }
}
